import { Ta_Maintenance_Type_Enum as MaintenanceType } from '@nemlia/hasura-gql/developer/graphql'
import moment, { Moment } from 'moment'
import { MaintenanceRow } from '@/operations/queries/getMaintenance'
import { MaintenanceInsertRow } from '@/operations/mutations/insertMaintenance'

export { MaintenanceType }

export const LANGUAGES = ['en', 'da', 'de', 'fo']
export const VARIABLES = ['maintenance_start', 'maintenance_end']
export const STATUSES = ['active', 'upcoming', 'expired'] as const
export const WEB_PLATFORM_NAME = 'Stýra'
// Ordered by severity, least first.
export const TYPES = [
  MaintenanceType.Info,
  MaintenanceType.Success,
  MaintenanceType.Warning,
  MaintenanceType.Error,
]

export const variableLabelKey = (variable: string) => `styra.input.${variable}.label`

export const fillVariables = (text: string, values: Record<string, string>) =>
  text.replace(/\{\{([^{}]+)\}\}/g, (token, name: string) => values[name] ?? token)

const usesVariables = (text: string) =>
  VARIABLES.some((variable) => text.includes(`{{${variable}}}`))

export type MaintenanceStatus = (typeof STATUSES)[number]

export interface MaintenanceLanguage {
  language: string
  text: string
  displayStart: Moment | null
  displayEnd: Moment | null
}

// One banner as the user sees it. The table stores it as a row per language and organisation.
export interface MaintenanceItem {
  ids: string[]
  groupId: string
  type: MaintenanceType
  // At least one must be true (database constraint).
  showInApp: boolean
  showInWeb: boolean
  // Unpublished rows are only visible to developers and suppliers.
  published: boolean
  // Optional, unless the text uses VARIABLES.
  maintenanceStart: Moment | null
  maintenanceEnd: Moment | null
  languages: MaintenanceLanguage[]
  // null means every organisation (rows with organisation_id null)
  organisationIds: string[] | null
}

// The columns are `timestamp` without a time zone and hold UTC.
export const fromDb = (value: string): Moment => moment(moment.utc(value).toDate())
export const toDb = (value: Moment): string => value.clone().utc().format('YYYY-MM-DDTHH:mm:ss')

export const emptyMaintenanceItem = (): MaintenanceItem => ({
  ids: [],
  groupId: crypto.randomUUID(),
  type: MaintenanceType.Warning,
  showInApp: true,
  showInWeb: true,
  published: false,
  maintenanceStart: null,
  maintenanceEnd: null,
  languages: [],
  organisationIds: null,
})

export const windowVariables = (
  item: Pick<MaintenanceItem, 'maintenanceStart' | 'maintenanceEnd'>
): Record<string, string> =>
  item.maintenanceStart && item.maintenanceEnd
    ? {
        maintenance_start: item.maintenanceStart.format('MMM D, HH:mm'),
        maintenance_end: item.maintenanceEnd.format('MMM D, HH:mm'),
      }
    : {}

// Rows sharing a group_id form one item, the same way the banner groups them.
export const groupMaintenanceRows = (rows: MaintenanceRow[]): MaintenanceItem[] => {
  const groups = new Map<string, MaintenanceRow[]>()
  rows.forEach((row) => {
    groups.set(row.group_id, [...(groups.get(row.group_id) ?? []), row])
  })

  return [...groups.values()].map((group) => {
    const languages = new Map<string, MaintenanceLanguage>()
    group.forEach((row) => {
      if (!languages.has(row.language)) {
        languages.set(row.language, {
          language: row.language,
          text: row.text,
          displayStart: fromDb(row.display_start),
          displayEnd: fromDb(row.display_end),
        })
      }
    })

    const organisationIds = group.flatMap((row) => row.organisation_id ?? [])

    const {
      group_id,
      type,
      published,
      show_in_app,
      show_in_web,
      maintenance_start,
      maintenance_end,
    } = group[0]

    return {
      ids: group.map((row) => row.id),
      groupId: group_id,
      type,
      showInApp: show_in_app,
      showInWeb: show_in_web,
      published,
      maintenanceStart: maintenance_start ? fromDb(maintenance_start) : null,
      maintenanceEnd: maintenance_end ? fromDb(maintenance_end) : null,
      languages: [...languages.values()].sort(
        (a, b) => LANGUAGES.indexOf(a.language) - LANGUAGES.indexOf(b.language)
      ),
      organisationIds: organisationIds.length < group.length ? null : [...new Set(organisationIds)],
    }
  })
}

// One row per language and organisation. Expects an item that passed isMaintenanceItemValid.
export const toMaintenanceRows = (item: MaintenanceItem): MaintenanceInsertRow[] =>
  item.languages.flatMap((language) =>
    (item.organisationIds ?? [null]).map((organisationId) => ({
      group_id: item.groupId,
      type: item.type,
      published: item.published,
      show_in_app: item.showInApp,
      show_in_web: item.showInWeb,
      language: language.language,
      text: language.text.trim(),
      display_start: toDb(language.displayStart!),
      display_end: toDb(language.displayEnd!),
      maintenance_start: item.maintenanceStart ? toDb(item.maintenanceStart) : null,
      maintenance_end: item.maintenanceEnd ? toDb(item.maintenanceEnd) : null,
      organisation_id: organisationId,
    }))
  )

const isRange = (start: Moment | null, end: Moment | null) =>
  Boolean(start?.isValid() && end?.isValid() && end.isAfter(start))

// The variables are filled from the window.
export const isWindowRequired = (item: Pick<MaintenanceItem, 'languages'>) =>
  item.languages.some((language) => usesVariables(language.text))

const isWindowValid = (item: MaintenanceItem) =>
  isRange(item.maintenanceStart, item.maintenanceEnd) ||
  (!item.maintenanceStart && !item.maintenanceEnd && !isWindowRequired(item))

export const isLanguageValid = (language: MaintenanceLanguage): boolean =>
  Boolean(language.text.trim()) && isRange(language.displayStart, language.displayEnd)

export const isMaintenanceItemValid = (item: MaintenanceItem): boolean =>
  isWindowValid(item) &&
  item.languages.length > 0 &&
  item.languages.every(isLanguageValid) &&
  item.organisationIds?.length !== 0

export const getDisplaySpan = (item: MaintenanceItem): { start: Moment; end: Moment } => ({
  start: moment.min(item.languages.map((language) => language.displayStart!)),
  end: moment.max(item.languages.map((language) => language.displayEnd!)),
})

export const getMaintenanceStatus = (item: MaintenanceItem, now: number): MaintenanceStatus => {
  const { start, end } = getDisplaySpan(item)
  if (start.valueOf() > now) {
    return 'upcoming'
  }
  return end.valueOf() < now ? 'expired' : 'active'
}
